import cx from 'classnames';
import { Mood } from '@peekapak/types/Mood';
import type { SecondaryMood } from '@peekapak/types/Mood';
import { getEmotionColor, resolveSixToTwelveCategory } from '../../ConcerningEmotionsReportUtilities';
import type { WeeklySnapshot } from '../moodboardSnapshot';
import styles from './WeeklyClassSnapshot.module.scss';

export type GradeBand = 'elementary' | 'secondary';

interface WeeklyClassSnapshotProps {
  snapshot: WeeklySnapshot;
  gradeBand: GradeBand;
  week?: { label: string; onPrevious?: () => void; onNext?: () => void };
}

// Dark text, except on the darkest Secondary families
export const WHITE_TEXT_FAMILIES: string[] = [Mood.SCARED, Mood.ANGRY];

export const getChipColors = (mood: string, gradeBand: GradeBand) => {
  try {
    const feeling = mood as Mood | SecondaryMood;
    const { backgroundColor } = getEmotionColor(feeling, gradeBand === 'elementary' ? 'PK-5' : '6-12');
    const family = gradeBand === 'secondary' ? resolveSixToTwelveCategory(feeling) : undefined;
    return { backgroundColor, color: family && WHITE_TEXT_FAMILIES.includes(family) ? '#ffffff' : '#43484d' };
  } catch {
    return { backgroundColor: '#bbc2ca', color: '#43484d' };
  }
};

export const getMoodLabel = (mood: string) => mood.replace('/', ' / ');

const WeeklyClassSnapshot = ({ snapshot, gradeBand, week }: WeeklyClassSnapshotProps) => (
  <>
    {week && (
      <div className={styles.header}>
        <div>
          <h2>Weekly Class Snapshot</h2>
          <p>See how your class is feeling this week.</p>
        </div>
        <div className={cx(styles.weekPicker, styles[gradeBand])}>
          <button aria-label='Previous week' disabled={!week.onPrevious} onClick={week.onPrevious}>
            &lsaquo;
          </button>
          <span>{week.label}</span>
          <button aria-label='Next week' disabled={!week.onNext} onClick={week.onNext}>
            &rsaquo;
          </button>
        </div>
      </div>
    )}
    <div className={styles.tiles}>
      <div className={styles.tile}>
        <div className={styles.label}>Students who checked in</div>
        <div className={styles.value} data-testid='snapshot-checked-in'>
          {snapshot.checkedIn} <small>of {snapshot.rosterSize}</small>
        </div>
        <div className={styles.hint}>At least one check-in this week</div>
      </div>
      <div className={styles.tile}>
        <div className={styles.label}>Check-ins this week</div>
        <div className={styles.value} data-testid='snapshot-total'>
          {snapshot.totalCheckIns}
        </div>
        <div className={styles.hint}>Includes more than one check-in on the same day</div>
      </div>
      <div className={styles.tile}>
        <div className={styles.label}>Most common {gradeBand === 'elementary' ? 'moods' : 'feelings'}</div>
        <div className={styles.chips}>
          {snapshot.topMoods.map((mood) => (
            <span key={mood} className={styles.chip} style={getChipColors(mood, gradeBand)} data-testid='snapshot-mood'>
              {getMoodLabel(mood)}
            </span>
          ))}
        </div>
      </div>
      {gradeBand === 'secondary' && (
        <div className={styles.tile}>
          <div className={styles.label}>Most common sources</div>
          <div className={styles.chips}>
            {snapshot.topSources.map((source) => (
              <span key={source} className={styles.sourceChip} data-testid='snapshot-source'>
                {source}
              </span>
            ))}
          </div>
          <div className={styles.hint}>What students said is causing the feeling</div>
        </div>
      )}
    </div>
  </>
);

export default WeeklyClassSnapshot;
